'use client';

import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import tailwindConfig from '../../tailwind.config.js';
import { useSelector } from 'react-redux';
import { selectIsRobot } from 'store/index';

const TextRevealAnimation = ({ className, selector = 'p', ...props }) => {
  const boxRef = useRef(null);
  const splitText = useRef(null);
  const mm = useRef(null);
  const isRobot = useSelector(selectIsRobot);

  useEffect(() => {
    if (isRobot || !boxRef.current || typeof window === 'undefined') return;

    gsap.registerPlugin(ScrollTrigger);

    let ctx;

    mm.current = gsap.matchMedia();

    mm.current.add(`(min-width: ${tailwindConfig.theme.screens.lg})`, () => {
      boxRef.current.classList.remove('lg:opacity-0');

      ctx = gsap.context(() => {
        let mounted = true;

        import('gsap/SplitText').then(({ SplitText }) => {
          if (!mounted) return;

          gsap.registerPlugin(SplitText);

          const targets = boxRef.current.querySelectorAll(selector);
          if (!targets.length) return;

          splitText.current = new SplitText(targets, { type: 'lines' });

          gsap.from(splitText.current.lines, {
            y: 50,
            opacity: 0,
            stagger: 0.05,
            duration: 1.5,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: boxRef.current,
              start: 'top bottom',
              toggleActions: 'play pause resume reverse',
            },
          });
        });

        return () => {
          mounted = false;
        };
      }, boxRef);
    });

    return () => {
      splitText.current?.revert();
      splitText.current = null;

      ctx?.revert();
      mm.current?.kill();

      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [selector, isRobot]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < parseInt(tailwindConfig.theme.screens.lg, 10)) {
        splitText.current?.revert();
        splitText.current = null;
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return <div ref={boxRef} className={classNames(!isRobot && 'lg:opacity-0', className)} {...props} />;
};

export default TextRevealAnimation;
